'use client'

import { useMemo, useState } from 'react'
import { uploadFiles } from '@/utils/request'
import { toast } from '@/lib/toast'

const MAX_FILE_BYTES = 10 * 1024 * 1024

const buildKey = (productId: string, fieldName: string) => `${productId}-${fieldName}`

const normalizeFieldValue = (field: any, raw: any) => {
  if (field.type === 'file' && raw && typeof raw === 'object' && raw.url) return raw.url
  if (field.type === 'checkbox') return raw === 'true' || raw === true
  return raw
}

// productImage holds an image URL, or an array of them when the field is multiple.
const isFieldFilled = (field: any, value: any) => {
  if (Array.isArray(value)) return value.length > 0
  return !!value && value !== ''
}

export function useCustomFields() {
  const [productValues, setProductValues] = useState<{ [key: string]: any }>({})
  const [orderValues, setOrderValues] = useState<{ [key: string]: any }>({})
  const [uploadingProductFields, setUploadingProductFields] = useState<Set<string>>(new Set())
  const [uploadingOrderFields, setUploadingOrderFields] = useState<Set<string>>(new Set())

  const setProductField = (fieldKey: string, value: any) => {
    setProductValues(prev => ({ ...prev, [fieldKey]: value }))
  }

  const setOrderField = (fieldKey: string, value: any) => {
    setOrderValues(prev => ({ ...prev, [fieldKey]: value }))
  }

  const uploadFile = async (file: File): Promise<{ url: string; originalName: string } | null> => {
    if (file.size > MAX_FILE_BYTES) {
      toast.error('Файлын хэмжээ 10MB-с их байна')
      return null
    }
    try {
      const formData = new FormData()
      formData.append('images', file)
      const { data: uploadedFiles, error } = await uploadFiles({ formData, auth: false })
      if (error || !uploadedFiles) {
        toast.error(error || 'Файл хуулахад алдаа гарлаа')
        return null
      }
      const uploadedFile = Array.isArray(uploadedFiles) ? uploadedFiles[0] : uploadedFiles
      toast.success('Файл амжилттай хуулагдлаа')
      return { url: uploadedFile, originalName: file.name }
    } catch (error) {
      console.error('Error uploading file:', error)
      toast.error('Файл хуулахад алдаа гарлаа')
      return null
    }
  }

  const uploadProductFile = async (fieldKey: string, file: File | undefined) => {
    if (!file) return
    setUploadingProductFields(prev => new Set(prev).add(fieldKey))
    const result = await uploadFile(file)
    if (result) setProductValues(prev => ({ ...prev, [fieldKey]: result }))
    setUploadingProductFields(prev => {
      const next = new Set(prev)
      next.delete(fieldKey)
      return next
    })
  }

  const uploadOrderFile = async (fieldKey: string, file: File | undefined) => {
    if (!file) return
    setUploadingOrderFields(prev => new Set(prev).add(fieldKey))
    const result = await uploadFile(file)
    if (result) setOrderValues(prev => ({ ...prev, [fieldKey]: result }))
    setUploadingOrderFields(prev => {
      const next = new Set(prev)
      next.delete(fieldKey)
      return next
    })
  }

  const isUploadingProductField = (fieldKey: string) => uploadingProductFields.has(fieldKey)
  const isUploadingOrderField = (fieldKey: string) => uploadingOrderFields.has(fieldKey)

  const validateRequired = (products: any[], vendor: any, subscription: any): { ok: true } | { ok: false; error: string } => {
    if (!subscription?.canUseCustomFields) return { ok: true }
    for (const product of products) {
      const productFields = product?.fields || []
      for (const field of productFields) {
        if (!field.required) continue
        const value = productValues[buildKey(product.id, field.name)]
        if (field.type === 'file') {
          if (!value || !value.url) return { ok: false, error: `${product.name} барааны ${field.name} файл оруулна уу` }
        } else if (field.type === 'productImage') {
          if (!isFieldFilled(field, value)) return { ok: false, error: `${product.name} барааны ${field.name} зургаа сонгоно уу` }
        } else if (field.type !== 'checkbox') {
          if (!value || value === '') return { ok: false, error: `${product.name} барааны ${field.name} оруулна уу` }
        }
      }
    }
    if (vendor?.orderFields?.length > 0) {
      for (const field of vendor.orderFields) {
        if (!field.required) continue
        const value = orderValues[field.name]
        if (field.type === 'file') {
          if (!value || !value.url) return { ok: false, error: `${field.name} файл оруулна уу` }
        } else if (field.type === 'productImage') {
          if (!isFieldFilled(field, value)) return { ok: false, error: `${field.name} зургаа сонгоно уу` }
        } else if (field.type !== 'checkbox') {
          if (!value || value === '') return { ok: false, error: `${field.name} оруулна уу` }
        }
      }
    }
    return { ok: true }
  }

  const buildPayload = (products: any[], vendor: any) => {
    const productCustomFields: { [key: string]: any[] } = {}
    products.forEach((product: any) => {
      const productFields = product?.fields || []
      if (productFields.length === 0) return
      productCustomFields[product.id] = productFields.map((field: any) => ({
        name: field.name,
        value: normalizeFieldValue(field, productValues[buildKey(product.id, field.name)] || field.value || ''),
        type: field.type,
      }))
    })

    const orderCustomFields: any[] = []
    if (vendor?.orderFields?.length > 0) {
      vendor.orderFields.forEach((field: any) => {
        orderCustomFields.push({
          name: field.name,
          value: normalizeFieldValue(field, orderValues[field.name] || field.value || ''),
          type: field.type,
        })
      })
    }
    return { productCustomFields, orderCustomFields }
  }

  return useMemo(() => ({
    productValues,
    orderValues,
    setProductField,
    setOrderField,
    uploadProductFile,
    uploadOrderFile,
    isUploadingProductField,
    isUploadingOrderField,
    buildPayload,
    validateRequired,
    buildProductKey: buildKey,
  }), [productValues, orderValues, uploadingProductFields, uploadingOrderFields])
}
